import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { MdRefresh, MdPrint, MdComputer, MdLabel } from "react-icons/md";
import { ButtonIcon, LoadingDiv, TextInputField } from "../../../components";
import { PopUpAlert } from "../../../components/movableDragDropArea";
import { ThemeColor, BASE_SERVER_URL } from "../../../sampleData";
import "./style.css";

export default function PrinterConnection() {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [alert, setAlert] = useState({});
  const [defaultPrinters, setDefaultPrinters] = useState({ label_print: null });
  const [modal, setModal] = useState({ open: false, printer: null, selection: "label_print", loading: false });

  useEffect(() => {
    fetchPrinters();
    fetchDefaults();
  }, []);

  function fetchPrinters() {
    setLoading(true);
    axios.get(`${BASE_SERVER_URL}/api/printers`)
      .then(response => {
        if (response.data.success) {
          setPrinters(response.data.printers);
        }
      })
      .catch(err => console.error("Error fetching printers:", err))
      .finally(() => setLoading(false));
  }

  function fetchDefaults() {
    axios.get(`${BASE_SERVER_URL}/api/printers/defaults`)
      .then(response => {
        if (response.data.success) {
          setDefaultPrinters(response.data.defaults);
        }
      })
      .catch(err => console.error("Error fetching defaults:", err));
  }

  const filteredPrinters = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return printers;
    return printers.filter((p) => p.name.toLowerCase().includes(term));
  }, [printers, search]);

  function saveDefault(printer, task) {
    if (!task) return;
    setModal(m => ({ ...m, loading: true }));
    axios.post(`${BASE_SERVER_URL}/api/printers/defaults`, {
      task,
      printerId: printer.id
    })
      .then(response => {
        if (response.data.success) {
          fetchDefaults();
          setAlert({
            type: "success",
            title: "Success",
            message: "Default printer updated successfully",
          });
          setModal(m => ({ ...m, open: false, loading: false }));
        } else {
          setAlert({
            type: "error",
            title: "Error",
            message: response.data.message || "Failed to update default printer",
          });
          setModal(m => ({ ...m, loading: false }));
        }
      })
      .catch(err => {
        setAlert({
          type: "error",
          title: "Error",
          message: "Connection error. Please try again.",
        });
        setModal(m => ({ ...m, loading: false }));
      });
  }

  useEffect(() => {
    if (modal.open && modal.printer) {
      const isSelected = modal.selection === "label_print";
      const primaryColor = (Array.isArray(ThemeColor) ? ThemeColor[0] : ThemeColor) || "#b79856";
      setAlert((prev) => ({
        ...prev,
        confirmDisabled: !isSelected,
        confirmLoading: modal.loading,
        htmlMEssage: (
          <div className="printer-modal-list">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setModal(m => ({ ...m, selection: m.selection === "label_print" ? null : "label_print" }));
              }}
              className={`printer-modal-item ${isSelected ? "selected" : ""}`}
              style={{
                width: "100%",
                outline: "none",
                borderColor: isSelected ? primaryColor : "#eeeeee",
                backgroundColor: isSelected ? `${primaryColor}15` : "#ffffff",
                borderWidth: "1px",
                borderStyle: "solid",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "12px 15px",
                marginBottom: "8px",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                color: isSelected ? primaryColor : "#333333",
                pointerEvents: "auto",
                zIndex: 10
              }}
            >
              <span style={{ fontSize: "20px", display: "flex", alignItems: "center", color: isSelected ? primaryColor : "#888888", pointerEvents: "none" }}>
                <MdLabel />
              </span>
              <span style={{ fontWeight: isSelected ? "700" : "500", pointerEvents: "none" }}>
                Label Print
              </span>
            </button>
          </div>
        )
      }));
    }
  }, [modal.selection, modal.open, modal.printer, modal.loading]);

  function openModal(printer) {
    let currentSelection = "label_print";
    const primaryColor = (Array.isArray(ThemeColor) ? ThemeColor[0] : ThemeColor) || "#b79856";

    setModal({ open: true, printer, selection: currentSelection, loading: false });
    setAlert({
      title: "Set Default Printer Task",
      message: `Select task for ${printer.name}`,
      confirmPrompt: true,
      noIcon: true,
      cardClass: "light-bg",
      titleClass: "color-black font-medium",
      cancelValue: "Cancel",
      confirmValue: "Save",
      confirmDisabled: currentSelection !== "label_print",
      confirmLoading: false,
      htmlMEssage: (
        <div className="printer-modal-list">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setModal(m => ({ ...m, selection: m.selection === "label_print" ? null : "label_print" }));
            }}
            className={`printer-modal-item selected`}
            style={{
              width: "100%",
              outline: "none",
              borderColor: primaryColor,
              backgroundColor: `${primaryColor}15`,
              borderWidth: "1px",
              borderStyle: "solid",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "12px 15px",
              marginBottom: "8px",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              color: primaryColor,
              pointerEvents: "auto",
              zIndex: 10
            }}
          >
            <span style={{ fontSize: "20px", display: "flex", alignItems: "center", color: primaryColor, pointerEvents: "none" }}>
              <MdLabel />
            </span>
            <span style={{ fontWeight: "700", pointerEvents: "none" }}>
              Label Print
            </span>
          </button>
        </div>
      ),
      callback: () => {
        setModal(currentModal => {
          if (currentModal.selection) {
            saveDefault(printer, currentModal.selection);
          }
          return currentModal;
        });
      },
    });
  }

  return (
    <div className="printer-settings-view">
      {Object.keys(alert).length > 0 && (
        <PopUpAlert
          info={alert}
          onClose={() => {
            setAlert({});
          }}
        />
      )}

      <header className="golden-card" style={{ position: "relative" }}>
        Printer Connection
        <div style={{ position: "absolute", right: "10px", top: "8px", width: "35px" }}>
          <ButtonIcon
            addClass="small"
            icon={<MdRefresh className="icon mid" />}
            loading={loading}
            clickTo={fetchPrinters}
          />
        </div>
      </header>

      <div className="search-wrapper" style={{ padding: "10px 0" }}>
        <TextInputField
          addClass="width-100 height-thin flat"
          addInputClass="light-border"
          placeHolder={"Search printer by name"}
          name="search-printer"
          value={search}
          noLable={true}
          onChageSetter={(a) => setSearch(a.value)}
        />
      </div>

      <div className="printers-vertical-stack" style={{ minHeight: "200px" }}>
        {loading ? (
          <div style={{ width: "100%" }}>
            <LoadingDiv amount={4} noDelay={true} height="35px" />
          </div>
        ) : filteredPrinters.length === 0 ? (
          <div className="no-printers" style={{ textAlign: "center", padding: "20px", color: "#888" }}>No printers found.</div>
        ) : (
          filteredPrinters.map((printer) => {
            return (
              <div
                key={printer.id}
                className="printer-strip-row"
                onClick={() => openModal(printer)}
              >
                <div className="p-icon-box">
                  <MdPrint style={{ color: (Array.isArray(ThemeColor) ? ThemeColor[0] : ThemeColor) || "#b79856", fontSize: "24px" }} />
                </div>
                <div className="p-details-box">
                  <span className="p-name-txt">{printer.name}</span>
                  <div className="p-sub-details">
                    <MdComputer style={{ fontSize: "14px", color: "#888", marginRight: "5px" }} />
                    <span className="p-computer-txt">{printer.computer?.name || "Unknown Computer"}</span>
                  </div>
                </div>
                <div className="p-tag-box" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                  {defaultPrinters.label_print?.printerId === printer.id && (
                    <span className="label-printer-badge">
                      Default Label Printer
                    </span>
                  )}
                </div>
                <div className="p-status-right-box" style={{ marginLeft: "20px", display: "flex", alignItems: "center", gap: "10px", marginRight: "20px", flexShrink: 0 }}>
                  <span className="p-id-txt" style={{ fontSize: "12px", color: "#999" }}>ID: {printer.id}</span>
                  <span className="p-status-txt" style={{ fontSize: "12px", textTransform: "capitalize", color: "#666" }}>{printer.state}</span>
                  <span className="p-status-dot" style={{ backgroundColor: printer.state === "online" ? "#4caf50" : "#f44336", width: "8px", height: "8px", borderRadius: "50%" }}></span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
